import React, { useContext, useCallback, useEffect, useMemo } from 'react'
import Textarea from 'react-textarea-autosize'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { v4 } from 'uuid'
import { useForm, FormProvider } from 'react-hook-form'
import cx from 'classnames'

import { StepContext } from 'Src/containers/contexts'
import { setFlowHistory, deleteAction, updateAction, addAction, createStep } from 'Actions/flow'
import { getInternalNames, getStepNames } from 'Selectors/flow'
import ActionSelect from '../templates/action-select/ActionSelect'
import { findgoToStep } from '../../utils/findGoToStep'

const defaultAction = {
  type: 'goToStep'
}

const allowedActions = [
  'goToNewStep',
  'goToFlow',
  'goToStep',
  'submit',
  'link',
  'calendly_schedule_event',
  'happyfoxchat_transfer_to_department',
  'hide_no_agent_avl_action'
]

const startStepDisallowedActions = ['calendly_schedule_event', 'happyfoxchat_transfer_to_department']

const createQuickReply = (data) => {
  if (data.action?.type === 'happyfoxchat_transfer_to_department') {
    if (data.action.no_agents_available_action.type === 'hide_no_agent_avl_action') {
      return {
        ...data,
        text: data.text.trim(),
        action: {
          type: data.action.type,
          hideOptionIfNoAgentsAvl: true
        }
      }
    }
  }

  if (data.action?.type === 'goToFlow') {
    return {
      ...data,
      text: data.text.trim(),
      action: {
        type: 'goToStep',
        flowId: data.action.flowId,
        step: data.action.step
      }
    }
  }

  if (data.action?.type === 'goToNewStep') {
    return {
      ...data,
      text: data.text.trim(),
      action: {
        type: 'goToStep',
        step: v4()
      }
    }
  }

  return {
    ...data,
    text: data.text.trim()
  }
}

const parseQuickReply = (quickReply) => {
  if (quickReply.action?.type === 'happyfoxchat_transfer_to_department') {
    if (quickReply.action.hideOptionIfNoAgentsAvl) {
      return {
        ...quickReply,
        action: {
          type: quickReply.action.type,
          no_agents_available_action: {
            type: 'hide_no_agent_avl_action'
          }
        }
      }
    }
  }

  if (quickReply.action?.type === 'goToStep' && quickReply.action.flowId) {
    return {
      ...quickReply,
      action: {
        ...quickReply.action,
        type: 'goToFlow'
      }
    }
  }

  return quickReply
}

const QuickReplyEdit = (props) => {
  const { mode = 'edit', actionId = null, choiceId = null, quickReply = {}, cancelFn = _.noop } = props
  const showDeleteBtn = mode === 'edit'

  const { stepId, step, row } = useContext(StepContext)
  const dispatch = useDispatch()
  const internalNames = useSelector(getInternalNames)
  const stepNames = useSelector(getStepNames)

  const supporedActions = useMemo(() => {
    if (step.name !== 'start') {
      return allowedActions
    }

    return allowedActions.filter((k) => !startStepDisallowedActions.includes(k))
  }, [step])

  const setpSettings = _.get(step, 'settings', {})
  const currentChoice = _.get(setpSettings, `${actionId}.settings.${choiceId}`, {})

  const form = useForm({
    defaultValues: _.cloneDeep(parseQuickReply(quickReply))
  })

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = form

  const getUniqueInternalName = (name, count = 0) => {
    const uniqueName = count ? `${name}-${count}` : name
    if (internalNames.includes(uniqueName)) {
      return getUniqueInternalName(uniqueName, count + 1)
    } else {
      return uniqueName
    }
  }

  const getUniqueStepName = (name, count = 0) => {
    const uniqueName = count ? `${name} (${count})` : name
    if (stepNames.includes(uniqueName)) {
      return getUniqueStepName(uniqueName, count + 1)
    } else {
      return uniqueName
    }
  }

  function onFormSubmit(data) {
    const choice = createQuickReply(data)

    if (data.action?.type === 'goToNewStep' && choice.action.type === 'goToStep') {
      const stepId = choice.action.step

      const stepName = getUniqueStepName(choice.text)
      const internalName = getUniqueInternalName(_.kebabCase(choice.text))

      dispatch(
        createStep({
          stepId,
          stepName,
          internalName
        })
      )
    }

    if (mode === 'edit') {
      update(choice)
    } else {
      create(choice)
    }
  }

  const updateFlowHistory = useCallback(
    (goToStep, r, skip = false) => {
      if (skip) {
        return
      }

      dispatch(setFlowHistory({ goToStep, row: r }))
    },
    [dispatch]
  )

  const update = useCallback(
    (choice) => {
      const action = _.get(setpSettings, `${actionId}`)

      dispatch(
        updateAction({
          stepId,
          actionId,
          choiceId,
          setting: {
            ...action,
            settings: {
              ...action.settings,
              [choiceId]: choice
            }
          }
        })
      )

      updateFlowHistory(findgoToStep(choice.action), row, Boolean(choice.action?.flowId))
      cancelFn()
    },
    [dispatch, actionId, stepId, choiceId, row, updateFlowHistory, cancelFn, setpSettings]
  )

  const create = useCallback(
    (choice) => {
      const qrID = v4()

      const actions = _.get(step, 'actions', [])
      const lastActionId = _.last(actions)

      // if last action is a quick reply
      const isLastActionQrs = _.get(setpSettings, `${lastActionId}.type`) === 'quickReplies'

      if (!isLastActionQrs) {
        dispatch(
          addAction({
            stepId,
            actionId: v4(),
            setting: {
              type: 'quickReplies',
              choices: [qrID],
              settings: {
                [qrID]: choice
              }
            }
          })
        )
      } else {
        const action = _.get(setpSettings, `${lastActionId}`)

        dispatch(
          updateAction({
            stepId,
            actionId: lastActionId,
            setting: {
              ...action,
              choices: [...action.choices, qrID],
              settings: {
                ...action.settings,
                [qrID]: choice
              }
            }
          })
        )
      }

      updateFlowHistory(findgoToStep(choice.action), row, Boolean(choice.action?.flowId))
      cancelFn()
    },
    [dispatch, step, setpSettings, stepId, row, updateFlowHistory, cancelFn]
  )

  const onDelete = useCallback(() => {
    const action = _.get(setpSettings, `${actionId}`)
    const choices = _.get(action, 'choices', [])

    if (_.get(choices, 'length') === 1) {
      dispatch(
        deleteAction({
          stepId,
          actionId
        })
      )
    } else {
      dispatch(
        updateAction({
          stepId,
          actionId,
          setting: {
            ...action,
            choices: _.filter(action.choices, (id) => id !== choiceId),
            settings: _.omit(action.settings, [choiceId])
          }
        })
      )
    }

    updateFlowHistory(stepId, row - 1)
  }, [actionId, dispatch, setpSettings, stepId, choiceId, row, updateFlowHistory])

  useEffect(() => {
    const goToStep = findgoToStep(currentChoice.action || defaultAction)
    if (mode === 'create' || goToStep === undefined) {
      const currentRow = row - 1
      updateFlowHistory(stepId, currentRow)
    }
  }, [dispatch, row, stepId, mode, currentChoice.action, updateFlowHistory])

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <div className='form-group'>
          <div className='label'>User choice</div>
          <Textarea
            maxRows={2}
            minRows={1}
            className={cx('form-control', { 'is-invalid': errors.text })}
            placeholder='Enter a label for the choice'
            {...register('text', { required: 'Choice label is required' })}
          />
          <div className='invalid-feedback'>{errors.text?.message}</div>
        </div>
        <div className='select-action'>
          <div className='label'>Action on selecting a choice</div>
          <ActionSelect allowedActions={supporedActions} isQuickReplay />
        </div>
        <div className='options'>
          <button className='button-primary small save' type='submit'>
            Save
          </button>
          <button className='btn cancel' onClick={cancelFn} type='button'>
            Cancel
          </button>
          {showDeleteBtn && (
            <button className='btn delete' type='button' onClick={onDelete}>
              Delete
            </button>
          )}
        </div>
      </form>
    </FormProvider>
  )
}

export default QuickReplyEdit
